/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Kayıt geçmişi yalnızca kaydedilmiş entity'nin İncele/Düzenle formundan açılır.
 * DataTable satır aksiyonu veya sağ tık menüsü bu sözleşmenin parçası değildir.
 *
 * Görünüm iki kurumsal tabloya ayrılır:
 * 1. Olaylar: bir satır = bir iş olayı.
 * 2. Alan değişiklikleri: bir satır = bir alan/hücre değişikliği.
 */

import React, { useEffect, useId, useMemo, useState } from 'react';
import axios from '../../api/axios';
import { COMPANY_SCOPE_HEADER } from '../../config/company-scope.config';
import type { SupportedLanguage } from '../../utils/language.utils';
import { Alert } from './Alert';
import { DataTable, type Column } from './DataTable';
import { Modal } from './Modal';
import { PageTabs, type PageTabItem } from './PageTabs';
import { Pagination } from './Pagination';
import {
  PAGE_SIZE_OPTIONS,
  useUserPageSizePreference,
} from '../../hooks/useUserPageSizePreference';
import './EntityAuditHistoryModal.css';
import { activitySourceDisplay, activitySourceLabels, type ActivitySourceView } from './activity-source-display';
import { AiWorkHistoryTab, aiWorkLabels } from './AiWorkHistoryTab';

type ActivityOutcome = 'SUCCESS' | 'FAILURE' | 'DENIED';
type HistoryTab = 'events' | 'changes' | 'ai' | 'supplementary';

type EntityHistoryEvent = {
  id: string;
  eventId: string;
  action?: string | null;
  module?: string | null;
  actorType?: string | null;
  actorName?: string | null;
  actorEmail?: string | null;
  outcome?: ActivityOutcome;
  reason?: string | null;
  applicationCode?: string | null;
  sourceProvenance?: ActivitySourceView | null;
  occurredAt: string;
  changeCount: number;
  changeFields: string[];
};

type EntityHistoryChange = EntityHistoryEvent & {
  field: string;
  oldValue: unknown;
  newValue: unknown;
};

type SelectedHistoryEvent = {
  eventId: string;
  actor: string;
  occurredAt: string;
};

type HistorySortState = {
  column: string;
  direction: 'asc' | 'desc';
};

type EventColumnFilters = {
  occurredDate: string;
  actor: string;
  action: string;
  changeField: string;
  reason: string;
  outcome: string;
};

type ChangeColumnFilters = {
  occurredDate: string;
  actor: string;
  field: string;
  oldValue: string;
  newValue: string;
  action: string;
};

const EMPTY_EVENT_FILTERS: EventColumnFilters = {
  occurredDate: '',
  actor: '',
  action: '',
  changeField: '',
  reason: '',
  outcome: '',
};

const EMPTY_CHANGE_FILTERS: ChangeColumnFilters = {
  occurredDate: '',
  actor: '',
  field: '',
  oldValue: '',
  newValue: '',
  action: '',
};

export interface EntityAuditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationCode: string;
  entityType: string;
  entityId: string | number | null | undefined;
  /** Merkezi ekranlarda kaydın ait olduğu yetkili iş firması kapsamı. */
  companyId?: string | number | null;
  entityName?: string | null;
  language?: SupportedLanguage;
  /** Teknik audit alan kodunu formdaki kullanıcı etiketine dönüştürür. */
  fieldLabels?: Record<string, string>;
  /** Optional read-only history view supplied by the record owner. */
  supplementaryTab?: {
    label: string;
    render: () => React.ReactNode;
  };
}

function readPagedResponse<T>(payload: any): { items: T[]; total: number } {
  const body = payload?.data ?? payload;
  const items = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.items)
        ? body.items
        : [];
  const meta = payload?.meta ?? body?.meta;
  const total = Number(meta?.total ?? body?.total ?? items.length);
  return { items, total: Number.isFinite(total) ? total : items.length };
}

function displayAuditValue(value: unknown, language: SupportedLanguage): string {
  if (value === null || value === undefined || value === '') return language === 'tr' ? 'Boş' : 'Empty';
  if (typeof value === 'boolean') {
    return value
      ? (language === 'tr' ? 'Evet' : 'Yes')
      : (language === 'tr' ? 'Hayır' : 'No');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function actionLabel(action: string | null | undefined, language: SupportedLanguage): string {
  const normalized = String(action || '').toUpperCase();
  const labels: Record<string, [string, string]> = {
    CREATE: ['Oluşturuldu', 'Created'],
    UPDATE: ['Güncellendi', 'Updated'],
    DELETE: ['Silindi', 'Deleted'],
    RESTORE: ['Geri getirildi', 'Restored'],
    REVERSE: ['Ters kayıt', 'Reversed'],
    APPROVE: ['Onaylandı', 'Approved'],
    REJECT: ['Reddedildi', 'Rejected'],
    SUBMIT_APPROVAL: ['Onaya gönderildi', 'Submitted for approval'],
    SEND: ['Gönderildi', 'Sent'],
    ACCEPT: ['Kabul edildi', 'Accepted'],
    REVISE: ['Yeni revizyon oluşturuldu', 'New revision created'],
    CREATE_LINE: ['Satır eklendi', 'Line added'],
    UPDATE_LINE: ['Satır güncellendi', 'Line updated'],
    DELETE_LINE: ['Satır silindi', 'Line deleted'],
  };
  const selected = labels[normalized];
  return selected
    ? selected[language === 'tr' ? 0 : 1]
    : (normalized || (language === 'tr' ? 'İşlem' : 'Action'));
}

function outcomeLabel(outcome: string | null | undefined, language: SupportedLanguage): string {
  if (outcome === 'FAILURE') return language === 'tr' ? 'Başarısız' : 'Failed';
  if (outcome === 'DENIED') return language === 'tr' ? 'Reddedildi' : 'Denied';
  return language === 'tr' ? 'Başarılı' : 'Successful';
}

function actorLabel(item: Pick<EntityHistoryEvent, 'actorName' | 'actorEmail' | 'actorType'>, language: SupportedLanguage): string {
  return item.actorName
    || item.actorEmail
    || item.actorType
    || (language === 'tr' ? 'Sistem' : 'System');
}

function technicalFieldLabel(field: string, language: SupportedLanguage): string {
  const customPrefix = 'customFields.';
  const raw = field.startsWith(customPrefix)
    ? `${language === 'tr' ? 'Ek Alan' : 'Custom Field'} · ${field.slice(customPrefix.length)}`
    : field;
  return raw
    .replace(/[._-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatEventDate(value: string, language: SupportedLanguage): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString(language)
    : value;
}

function resolveFieldFilter(
  value: string,
  fieldLabels: Record<string, string>,
  language: SupportedLanguage,
): string {
  const normalized = value.trim().toLocaleLowerCase(language);
  if (!normalized) return '';
  const match = Object.entries(fieldLabels).find(([field, label]) =>
    label.toLocaleLowerCase(language).includes(normalized)
    || technicalFieldLabel(field, language).toLocaleLowerCase(language).includes(normalized));
  return match?.[0] || value.trim();
}

export const EntityAuditHistoryModal: React.FC<EntityAuditHistoryModalProps> = ({
  isOpen,
  onClose,
  applicationCode,
  entityType,
  entityId,
  companyId,
  entityName,
  language = 'tr',
  fieldLabels = {},
  supplementaryTab,
}) => {
  const [activeTab, setActiveTab] = useState<HistoryTab>('events');
  const [events, setEvents] = useState<EntityHistoryEvent[]>([]);
  const [changes, setChanges] = useState<EntityHistoryChange[]>([]);
  const [eventTotal, setEventTotal] = useState(0);
  const [changeTotal, setChangeTotal] = useState(0);
  const [eventPage, setEventPage] = useState(1);
  const [changePage, setChangePage] = useState(1);
  const [eventFilterInputs, setEventFilterInputs] = useState<EventColumnFilters>(EMPTY_EVENT_FILTERS);
  const [changeFilterInputs, setChangeFilterInputs] = useState<ChangeColumnFilters>(EMPTY_CHANGE_FILTERS);
  const [eventFilters, setEventFilters] = useState<EventColumnFilters>(EMPTY_EVENT_FILTERS);
  const [changeFilters, setChangeFilters] = useState<ChangeColumnFilters>(EMPTY_CHANGE_FILTERS);
  const [eventSort, setEventSort] = useState<HistorySortState>({
    column: 'occurredAt',
    direction: 'desc',
  });
  const [changeSort, setChangeSort] = useState<HistorySortState>({
    column: 'occurredAt',
    direction: 'desc',
  });
  const [selectedEvent, setSelectedEvent] = useState<SelectedHistoryEvent | null>(null);
  const [eventRefreshToken, setEventRefreshToken] = useState(0);
  const [changeRefreshToken, setChangeRefreshToken] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { pageSize, setPageSize } = useUserPageSizePreference();
  const tabId = useId();
  const historyPanelId = `${tabId}-panel`;
  const normalizedEntityId = entityId == null ? '' : String(entityId);
  const normalizedCompanyId = companyId == null ? '' : String(companyId).trim();

  const fieldLabel = (field: string) =>
    fieldLabels[field] || technicalFieldLabel(field, language);

  const eventChangeFieldQuery = useMemo(
    () => resolveFieldFilter(eventFilters.changeField, fieldLabels, language),
    [eventFilters.changeField, fieldLabels, language],
  );
  const changeFieldQuery = useMemo(
    () => resolveFieldFilter(changeFilters.field, fieldLabels, language),
    [changeFilters.field, fieldLabels, language],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setEventFilters(eventFilterInputs);
      setEventPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [eventFilterInputs]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setChangeFilters(changeFilterInputs);
      setChangePage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [changeFilterInputs]);

  useEffect(() => {
    if (isOpen) return;
    setActiveTab('events');
    setEvents([]);
    setChanges([]);
    setEventTotal(0);
    setChangeTotal(0);
    setEventPage(1);
    setChangePage(1);
    setEventFilterInputs(EMPTY_EVENT_FILTERS);
    setChangeFilterInputs(EMPTY_CHANGE_FILTERS);
    setEventFilters(EMPTY_EVENT_FILTERS);
    setChangeFilters(EMPTY_CHANGE_FILTERS);
    setEventSort({ column: 'occurredAt', direction: 'desc' });
    setChangeSort({ column: 'occurredAt', direction: 'desc' });
    setSelectedEvent(null);
    setError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'events' || !normalizedEntityId) return;
    let cancelled = false;
    setLoadingEvents(true);
    setError(null);
    axios.get('/api/activity-logs/entity-history/events', {
      headers: normalizedCompanyId
        ? { [COMPANY_SCOPE_HEADER]: normalizedCompanyId }
        : undefined,
      params: {
        applicationCode,
        entityType,
        entityId: normalizedEntityId,
        page: eventPage,
        size: pageSize,
        action: eventFilters.action || undefined,
        outcome: eventFilters.outcome || undefined,
        actorSearch: eventFilters.actor || undefined,
        changeField: eventChangeFieldQuery || undefined,
        reasonSearch: eventFilters.reason || undefined,
        startDate: eventFilters.occurredDate || undefined,
        endDate: eventFilters.occurredDate || undefined,
        sortBy: eventSort.column,
        sortDirection: eventSort.direction,
      },
    })
      .then((response) => {
        if (cancelled) return;
        const result = readPagedResponse<EntityHistoryEvent>(response.data);
        setEvents(result.items);
        setEventTotal(result.total);
      })
      .catch((requestError: any) => {
        if (cancelled) return;
        setEvents([]);
        setEventTotal(0);
        setError(
          requestError?.response?.data?.message
          || requestError?.response?.data?.error
          || requestError?.message
          || (language === 'tr' ? 'Olay geçmişi yüklenemedi.' : 'Event history could not be loaded.'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingEvents(false);
      });
    return () => { cancelled = true; };
  }, [
    activeTab,
    applicationCode,
    entityType,
    eventChangeFieldQuery,
    eventFilters,
    eventPage,
    eventRefreshToken,
    eventSort,
    isOpen,
    language,
    normalizedEntityId,
    normalizedCompanyId,
  ]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'changes' || !normalizedEntityId) return;
    let cancelled = false;
    setLoadingChanges(true);
    setError(null);
    axios.get('/api/activity-logs/entity-history/changes', {
      headers: normalizedCompanyId
        ? { [COMPANY_SCOPE_HEADER]: normalizedCompanyId }
        : undefined,
      params: {
        applicationCode,
        entityType,
        entityId: normalizedEntityId,
        page: changePage,
        size: pageSize,
        action: changeFilters.action || undefined,
        actorSearch: changeFilters.actor || undefined,
        startDate: changeFilters.occurredDate || undefined,
        endDate: changeFilters.occurredDate || undefined,
        eventId: selectedEvent?.eventId || undefined,
        changeField: changeFieldQuery || undefined,
        oldValueSearch: changeFilters.oldValue || undefined,
        newValueSearch: changeFilters.newValue || undefined,
        sortBy: changeSort.column,
        sortDirection: changeSort.direction,
      },
    })
      .then((response) => {
        if (cancelled) return;
        const result = readPagedResponse<EntityHistoryChange>(response.data);
        setChanges(result.items);
        setChangeTotal(result.total);
      })
      .catch((requestError: any) => {
        if (cancelled) return;
        setChanges([]);
        setChangeTotal(0);
        setError(
          requestError?.response?.data?.message
          || requestError?.response?.data?.error
          || requestError?.message
          || (language === 'tr' ? 'Alan geçmişi yüklenemedi.' : 'Field history could not be loaded.'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingChanges(false);
      });
    return () => { cancelled = true; };
  }, [
    activeTab,
    applicationCode,
    changeFieldQuery,
    changeFilters,
    changePage,
    changeRefreshToken,
    changeSort,
    entityType,
    isOpen,
    language,
    normalizedEntityId,
    normalizedCompanyId,
    selectedEvent?.eventId,
  ]);

  const sortEventsBy = (column: keyof EntityHistoryEvent) => {
    const nextColumn = String(column);
    setEventSort((current) => ({
      column: nextColumn,
      direction: current.column === nextColumn && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setEventPage(1);
  };

  const sortChangesBy = (column: keyof EntityHistoryChange) => {
    const nextColumn = String(column);
    setChangeSort((current) => ({
      column: nextColumn,
      direction: current.column === nextColumn && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setChangePage(1);
  };

  const selectHistoryEvent = (item: EntityHistoryEvent) => {
    setSelectedEvent({
      eventId: item.eventId,
      actor: actorLabel(item, language),
      occurredAt: item.occurredAt,
    });
    setChangePage(1);
    setActiveTab('changes');
  };

  const eventColumns = useMemo<Column<EntityHistoryEvent>[]>(() => [
    {
      key: 'occurredAt',
      title: language === 'tr' ? 'Zaman' : 'Time',
      width: '170px',
      render: (item) => formatEventDate(item.occurredAt, language),
      getTitle: (item) => formatEventDate(item.occurredAt, language),
      filterValue: eventFilterInputs.occurredDate,
      renderFilter: () => (
        <input
          type="date"
          className="column-filter"
          value={eventFilterInputs.occurredDate}
          aria-label={language === 'tr' ? 'Olay tarihini filtrele' : 'Filter event date'}
          onChange={(event) => setEventFilterInputs((current) => ({
            ...current,
            occurredDate: event.target.value,
          }))}
        />
      ),
    },
    {
      key: 'actorName',
      title: language === 'tr' ? 'Değiştiren' : 'Changed by',
      width: '180px',
      render: (item) => actorLabel(item, language),
      getTitle: (item) => actorLabel(item, language),
      filterValue: eventFilterInputs.actor,
      onFilterChange: (value) => setEventFilterInputs((current) => ({ ...current, actor: value })),
    },
    {
      key: 'source',
      title: activitySourceLabels(language).source,
      width: '110px',
      render: (item) => activitySourceDisplay(item.sourceProvenance, item.applicationCode, language).source,
      filterable: false,
    },
    {
      key: 'sourceDetail',
      title: activitySourceLabels(language).detail,
      width: '180px',
      render: (item) => activitySourceDisplay(item.sourceProvenance, item.applicationCode, language).detail,
      getTitle: (item) => activitySourceDisplay(item.sourceProvenance, item.applicationCode, language).title,
      filterable: false,
    },
    {
      key: 'action',
      title: language === 'tr' ? 'İşlem' : 'Action',
      width: '125px',
      render: (item) => actionLabel(item.action, language),
      filterType: 'select',
      filterValue: eventFilterInputs.action,
      onFilterChange: (value) => setEventFilterInputs((current) => ({ ...current, action: value })),
      filterOptions: [
        { value: 'CREATE', label: language === 'tr' ? 'Oluşturuldu' : 'Created' },
        { value: 'UPDATE', label: language === 'tr' ? 'Güncellendi' : 'Updated' },
        { value: 'DELETE', label: language === 'tr' ? 'Silindi' : 'Deleted' },
        { value: 'RESTORE', label: language === 'tr' ? 'Geri getirildi' : 'Restored' },
        { value: 'REVERSE', label: language === 'tr' ? 'Ters kayıt' : 'Reversed' },
        { value: 'APPROVE', label: language === 'tr' ? 'Onaylandı' : 'Approved' },
        { value: 'REJECT', label: language === 'tr' ? 'Reddedildi' : 'Rejected' },
      ],
    },
    {
      key: 'changeFields',
      title: language === 'tr' ? 'Değişen alan özeti' : 'Changed fields',
      width: '320px',
      render: (item) => {
        if (item.changeCount === 0) {
          return language === 'tr' ? 'Alan değişikliği yok' : 'No field change';
        }
        const labels = item.changeFields.map(fieldLabel);
        const visible = labels.slice(0, 3).join(', ');
        const remaining = Math.max(0, item.changeCount - 3);
        return remaining > 0 ? `${visible} +${remaining}` : visible;
      },
      getTitle: (item) => item.changeFields.map(fieldLabel).join(', '),
      filterValue: eventFilterInputs.changeField,
      onFilterChange: (value) => setEventFilterInputs((current) => ({ ...current, changeField: value })),
    },
    {
      key: 'reason',
      title: language === 'tr' ? 'Gerekçe' : 'Reason',
      defaultHidden: true,
      render: (item) => item.reason || '—',
      getTitle: (item) => item.reason || undefined,
      filterValue: eventFilterInputs.reason,
      onFilterChange: (value) => setEventFilterInputs((current) => ({ ...current, reason: value })),
    },
    {
      key: 'outcome',
      title: language === 'tr' ? 'Sonuç' : 'Outcome',
      width: '110px',
      render: (item) => (
        <span className={`entity-audit-history-outcome is-${String(item.outcome || 'SUCCESS').toLowerCase()}`}>
          {outcomeLabel(item.outcome, language)}
        </span>
      ),
      filterType: 'select',
      filterValue: eventFilterInputs.outcome,
      onFilterChange: (value) => setEventFilterInputs((current) => ({ ...current, outcome: value })),
      filterOptions: [
        { value: 'SUCCESS', label: language === 'tr' ? 'Başarılı' : 'Successful' },
        { value: 'FAILURE', label: language === 'tr' ? 'Başarısız' : 'Failed' },
        { value: 'DENIED', label: language === 'tr' ? 'Reddedildi' : 'Denied' },
      ],
    },
    {
      key: 'applicationCode',
      title: language === 'tr' ? 'Ürün' : 'Product',
      width: '90px',
      render: (item) => item.applicationCode || '—',
      filterable: false,
    },
  ], [eventFilterInputs, fieldLabels, language]);

  const changeColumns = useMemo<Column<EntityHistoryChange>[]>(() => [
    {
      key: 'occurredAt',
      title: language === 'tr' ? 'Zaman' : 'Time',
      width: '170px',
      render: (item) => formatEventDate(item.occurredAt, language),
      getTitle: (item) => formatEventDate(item.occurredAt, language),
      filterValue: changeFilterInputs.occurredDate,
      renderFilter: () => (
        <input
          type="date"
          className="column-filter"
          value={changeFilterInputs.occurredDate}
          aria-label={language === 'tr' ? 'Değişiklik tarihini filtrele' : 'Filter change date'}
          onChange={(event) => setChangeFilterInputs((current) => ({
            ...current,
            occurredDate: event.target.value,
          }))}
        />
      ),
    },
    {
      key: 'actorName',
      title: language === 'tr' ? 'Değiştiren' : 'Changed by',
      width: '170px',
      render: (item) => actorLabel(item, language),
      getTitle: (item) => actorLabel(item, language),
      filterValue: changeFilterInputs.actor,
      onFilterChange: (value) => setChangeFilterInputs((current) => ({ ...current, actor: value })),
    },
    {
      key: 'source',
      title: activitySourceLabels(language).source,
      width: '110px',
      render: (item) => activitySourceDisplay(item.sourceProvenance, item.applicationCode, language).source,
      filterable: false,
    },
    {
      key: 'sourceDetail',
      title: activitySourceLabels(language).detail,
      width: '180px',
      render: (item) => activitySourceDisplay(item.sourceProvenance, item.applicationCode, language).detail,
      getTitle: (item) => activitySourceDisplay(item.sourceProvenance, item.applicationCode, language).title,
      filterable: false,
    },
    {
      key: 'field',
      title: language === 'tr' ? 'Alan' : 'Field',
      width: '180px',
      render: (item) => fieldLabel(item.field),
      getTitle: (item) => fieldLabel(item.field),
      filterValue: changeFilterInputs.field,
      onFilterChange: (value) => setChangeFilterInputs((current) => ({ ...current, field: value })),
    },
    {
      key: 'oldValue',
      title: language === 'tr' ? 'Eski değer' : 'Old value',
      width: '240px',
      render: (item) => displayAuditValue(item.oldValue, language),
      getTitle: (item) => displayAuditValue(item.oldValue, language),
      filterValue: changeFilterInputs.oldValue,
      onFilterChange: (value) => setChangeFilterInputs((current) => ({ ...current, oldValue: value })),
    },
    {
      key: 'newValue',
      title: language === 'tr' ? 'Yeni değer' : 'New value',
      width: '240px',
      render: (item) => displayAuditValue(item.newValue, language),
      getTitle: (item) => displayAuditValue(item.newValue, language),
      filterValue: changeFilterInputs.newValue,
      onFilterChange: (value) => setChangeFilterInputs((current) => ({ ...current, newValue: value })),
    },
    {
      key: 'action',
      title: language === 'tr' ? 'İşlem' : 'Action',
      width: '120px',
      render: (item) => actionLabel(item.action, language),
      filterType: 'select',
      filterValue: changeFilterInputs.action,
      onFilterChange: (value) => setChangeFilterInputs((current) => ({ ...current, action: value })),
      filterOptions: [
        { value: 'CREATE', label: language === 'tr' ? 'Oluşturuldu' : 'Created' },
        { value: 'UPDATE', label: language === 'tr' ? 'Güncellendi' : 'Updated' },
        { value: 'DELETE', label: language === 'tr' ? 'Silindi' : 'Deleted' },
        { value: 'RESTORE', label: language === 'tr' ? 'Geri getirildi' : 'Restored' },
        { value: 'REVERSE', label: language === 'tr' ? 'Ters kayıt' : 'Reversed' },
        { value: 'APPROVE', label: language === 'tr' ? 'Onaylandı' : 'Approved' },
        { value: 'REJECT', label: language === 'tr' ? 'Reddedildi' : 'Rejected' },
      ],
    },
    {
      key: 'applicationCode',
      title: language === 'tr' ? 'Ürün' : 'Product',
      width: '90px',
      defaultHidden: true,
      render: (item) => item.applicationCode || '—',
      filterable: false,
    },
  ], [changeFilterInputs, fieldLabels, language]);

  const title = `${language === 'tr' ? 'Kayıt Geçmişi' : 'Record History'}${entityName ? ` · ${entityName}` : ''}`;
  const historyTabItems: PageTabItem<HistoryTab>[] = [
    {
      value: 'events',
      label: language === 'tr' ? 'Olay Geçmişi' : 'Event History',
      count: eventTotal > 0 ? eventTotal : undefined,
    },
    {
      value: 'changes',
      label: language === 'tr' ? 'Alan Değişiklikleri' : 'Field Changes',
      count: changeTotal > 0 ? changeTotal : undefined,
    },
    { value: 'ai', label: aiWorkLabels(language).tab },
  ];
  if (supplementaryTab) {
    historyTabItems.push({ value: 'supplementary', label: supplementaryTab.label });
  }
  const historyTabs = historyTabItems.map((tab) => ({
    ...tab,
    id: `${tabId}-${tab.value}`,
    ariaControls: historyPanelId,
  }));
  const totalPages = activeTab === 'events'
    ? Math.max(1, Math.ceil(eventTotal / pageSize))
    : Math.max(1, Math.ceil(changeTotal / pageSize));
  const totalItems = activeTab === 'events' ? eventTotal : changeTotal;
  const currentPage = activeTab === 'events' ? eventPage : changePage;
  const handlePageSizeChange = (nextPageSize: number) => {
    setEventPage(1);
    setChangePage(1);
    void setPageSize(nextPageSize);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="xl"
      closeOnOverlayClick={false}
      contentClassName="entity-audit-history-modal"
      bodyHeader={<PageTabs<HistoryTab>
        className="entity-audit-history-tabs"
        overflow="wrap"
        ariaLabel={title}
        items={historyTabs}
        value={activeTab}
        onChange={setActiveTab}
      />}
    >

      {selectedEvent && activeTab === 'changes' && (
        <div className="entity-audit-history-event-filter">
          <div>
            <strong>{language === 'tr' ? 'Seçili olay' : 'Selected event'}</strong>
            <span>{selectedEvent.actor} · {formatEventDate(selectedEvent.occurredAt, language)}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedEvent(null);
              setChangePage(1);
            }}
          >
            {language === 'tr' ? 'Tüm olayları göster' : 'Show all events'}
          </button>
        </div>
      )}

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}

      <div className={`entity-audit-history-table is-${activeTab}`} role="tabpanel"
        id={historyPanelId} aria-labelledby={`${tabId}-${activeTab}`}>
        {activeTab === 'events' ? (
          <DataTable
            data={events}
            columns={eventColumns}
            loading={loadingEvents}
            emptyMessage={language === 'tr' ? 'Bu kayıt için olay geçmişi bulunamadı.' : 'No event history was found.'}
            tableIdentity={{ module: 'audit', page: 'entityHistory', component: 'events' }}
            language={language}
            defaultColumnControls
            sortColumn={eventSort.column}
            sortDirection={eventSort.direction}
            onSort={sortEventsBy}
            isSortManuallyChanged
            rowMode="ellipsis"
            outputExport={{
              state: 'NOT_APPLICABLE',
              reasonCode: 'SECURITY_RESTRICTED',
              reason: 'Audit event exports require a dedicated field-level redaction contract.',
            }}
            onRefresh={() => setEventRefreshToken((current) => current + 1)}
            onRowClick={selectHistoryEvent}
          />
        ) : activeTab === 'changes' ? (
          <DataTable
            data={changes}
            columns={changeColumns}
            loading={loadingChanges}
            emptyMessage={language === 'tr' ? 'Filtreye uygun alan değişikliği bulunamadı.' : 'No matching field change was found.'}
            tableIdentity={{ module: 'audit', page: 'entityHistory', component: 'fieldChanges' }}
            language={language}
            defaultColumnControls
            sortColumn={changeSort.column}
            sortDirection={changeSort.direction}
            onSort={sortChangesBy}
            isSortManuallyChanged
            rowMode="ellipsis"
            outputExport={{
              state: 'NOT_APPLICABLE',
              reasonCode: 'SECURITY_RESTRICTED',
              reason: 'Before and after audit values require a dedicated field-level redaction contract.',
            }}
            onRefresh={() => setChangeRefreshToken((current) => current + 1)}
          />
        ) : activeTab === 'ai' ? (
          <AiWorkHistoryTab applicationCode={applicationCode} entityType={entityType}
            entityId={normalizedEntityId} companyId={normalizedCompanyId} language={language} />
        ) : supplementaryTab?.render()}
        {(activeTab === 'events' || activeTab === 'changes') && !loadingEvents && !loadingChanges && totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={activeTab === 'events' ? setEventPage : setChangePage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            language={language}
          />
        )}
      </div>
    </Modal>
  );
};

EntityAuditHistoryModal.displayName = 'EntityAuditHistoryModal';
